/**
 * TouchButton - Multi-Touch fähiger Button für Mobile Controls
 * Unterstützt gleichzeitiges Drücken mehrerer Buttons (z.B. Gas + Drift)
 */

import React, { useRef, useEffect, useState } from 'react';
import { Button, ButtonProps } from '@mui/material';

interface TouchButtonProps extends Omit<ButtonProps, 'onTouchStart' | 'onTouchEnd'> {
  onPress: (pressed: boolean) => void;
  label: string;
}

export const TouchButton: React.FC<TouchButtonProps> = ({
  onPress,
  label,
  ...buttonProps
}) => {
  const [pressed, setPressed] = useState(false);
  const touchIdRef = useRef<number | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const button = buttonRef.current;
    if (!button) return;

    const handleTouchStart = (e: TouchEvent) => {
      // Multi-Touch: Finde Touch innerhalb des Buttons
      if (touchIdRef.current !== null) return; // Button bereits gedrückt
      
      const rect = button.getBoundingClientRect();
      
      for (let i = 0; i < e.touches.length; i++) {
        const touch = e.touches[i];
        const x = touch.clientX;
        const y = touch.clientY;
        
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          e.preventDefault();
          e.stopPropagation();
          touchIdRef.current = touch.identifier;
          setPressed(true);
          onPress(true);
          break;
        }
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (touchIdRef.current === null) return;
      
      // Prüfe ob unser Touch beendet wurde
      let found = false;
      for (let i = 0; i < e.touches.length; i++) {
        if (e.touches[i].identifier === touchIdRef.current) {
          found = true;
          break;
        }
      }
      
      if (!found) {
        e.preventDefault();
        e.stopPropagation();
        touchIdRef.current = null;
        setPressed(false);
        onPress(false);
      }
    };

    // Registriere Touch Events
    button.addEventListener('touchstart', handleTouchStart, { passive: false });
    button.addEventListener('touchend', handleTouchEnd, { passive: false });
    button.addEventListener('touchcancel', handleTouchEnd, { passive: false });

    return () => {
      button.removeEventListener('touchstart', handleTouchStart);
      button.removeEventListener('touchend', handleTouchEnd);
      button.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [onPress]);

  return (
    <Button
      ref={buttonRef}
      {...buttonProps}
      variant={pressed ? 'contained' : 'outlined'}
      sx={{
        ...buttonProps.sx,
        touchAction: 'none',
        userSelect: 'none',
        transition: 'all 0.1s',
        transform: pressed ? 'scale(0.95)' : 'scale(1)',
        opacity: pressed ? 1 : 0.8
      }}
    >
      {label}
    </Button>
  );
};
